import { useRef } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { jsonParse } from '@lyearn/core/utils/json';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { GenerativeTaskFragment } from '@/components/LyearnAI/graphql/fragments/generated/GenerativeTaskFragment';
import useGenerativeTask from '@/components/LyearnAI/hooks/useGenerativeTask';
import { GenerativeTaskStatusEnum, GenerativeTaskTypeEnum } from '@/types/schema';

import { VideoSlideFromSelection } from './useVideoSlideSelection';

export interface SwitchVideoSlideTemplateTemplate {
  id: string;
  heygen_template_id: string;
  name: string;
  preview_url?: string | null;
  variableSchema?: string | null;
}

export interface SwitchVideoSlideTemplateContext {
  previousSlide: VideoSlideFromSelection;
  newSlideTemplateId: string;
  slideIndex: number;
  template?: SwitchVideoSlideTemplateTemplate;
}

interface UseSwitchVideoSlideTemplateProps {
  onSuccess?: (
    slide: VideoSlideFromSelection,
    context: SwitchVideoSlideTemplateContext,
  ) => void | Promise<void>;
  onError?: () => void;
}

export function useSwitchVideoSlideTemplate(props: UseSwitchVideoSlideTemplateProps = {}) {
  const { onSuccess, onError } = props;
  const { t } = useTranslation('Course');
  const { showErrorSnackbar } = useSnackbar();
  const latestContextRef = useRef<SwitchVideoSlideTemplateContext | null>(null);
  const handleTaskError = useEvent((message?: string | null) => {
    showErrorSnackbar({
      message: message || t('Course:AiVideoGeneration.FailedToSwitchVideoSlideTemplate'),
    });
  });

  const onSuccessEvent = useEvent(async (generativeTask: GenerativeTaskFragment) => {
    const slide = generativeTask.result.switchVideoSlideTemplate?.slide;
    const context = latestContextRef.current;
    latestContextRef.current = null;

    if (!slide || !context) {
      showErrorSnackbar({
        message: t('Course:AiVideoGeneration.FailedToSwitchVideoSlideTemplate'),
      });
      onError?.();
      return;
    }

    const parsedVariables = slide.variables
      ? jsonParse<Record<string, string>>(slide.variables).data ?? {}
      : {};

    await onSuccess?.(
      {
        ...slide,
        variables: parsedVariables,
      },
      context,
    );
  });

  const { executeCreateGenerativeTask, fetching, generativeTaskId, generativeTask } =
    useGenerativeTask({
      onError: () => {
        latestContextRef.current = null;
        onError?.();
      },
      onTaskError: (error) => {
        handleTaskError(error?.message);
      },
      onSuccess: onSuccessEvent,
      autoCleanup: true,
      suppressDefaultErrorHandling: true,
    });

  const switchVideoSlideTemplate = useEvent(
    (
      currentSlide: VideoSlideFromSelection,
      newSlideTemplateId: string,
      slideIndex: number,
      lessonContent?: string,
      userInstruction?: string,
      template?: SwitchVideoSlideTemplateTemplate,
    ) => {
      const normalizedSlideTemplateId = newSlideTemplateId.trim();
      if (!normalizedSlideTemplateId) {
        return;
      }

      latestContextRef.current = {
        previousSlide: currentSlide,
        newSlideTemplateId: normalizedSlideTemplateId,
        slideIndex,
        template,
      };

      const normalizedLessonContent = lessonContent?.trim() || undefined;
      const normalizedUserInstruction = userInstruction?.trim() || undefined;

      executeCreateGenerativeTask({
        type: GenerativeTaskTypeEnum.SwitchVideoSlideTemplate,
        input: {
          switchVideoSlideTemplate: {
            slideTemplateId: normalizedSlideTemplateId,
            previousSlide: {
              slideTemplateId: currentSlide.slideTemplateId,
              heygenTemplateId: currentSlide.heygenTemplateId,
              name: currentSlide.name,
              transcript: currentSlide.transcript,
              variables: currentSlide.variables
                ? JSON.stringify(currentSlide.variables)
                : undefined,
            },
            lessonContent: normalizedLessonContent,
            userInstruction: normalizedUserInstruction,
          },
        },
      });
    },
  );

  const isSwitchingVideoSlideTemplate =
    fetching ||
    (!!generativeTaskId && !generativeTask) ||
    (generativeTask &&
      [GenerativeTaskStatusEnum.Processing, GenerativeTaskStatusEnum.ToBeProcessed].includes(
        generativeTask.status,
      ));

  return {
    switchVideoSlideTemplate,
    isSwitchingVideoSlideTemplate,
  };
}
